import { GlobalMenu, DeleteDialog, CreateUpdatesDialog } from '@components';
import { useToast } from '@hooks';
import { NavigationContext } from '@layout/navigation/navigation';
import { IUpdateRawQueryList } from '@models/Update.model';
import { UpdateStatusType } from '@models/UpdateStatus.model';
import { changeUpdateStatus } from '@services/queries/updates/changeUpdateStatus/changeUpdateStatus';

import { useContext, useState } from 'react';
import { useQueryClient, useMutation } from 'react-query';
import { useParams } from 'react-router-dom';

import { deleteUpdate } from '@services/queries/updates';
import { useState as useHookState } from '@hookstate/core';
import { InviteUserRole } from '@models/Invite.model';
import { useAppDispatch } from '@store/redux/store';

import {
  addBackUpUpdatedUpdates,
  addDeletedUpdate,
  addUpdatedUpdate,
} from '@store/redux/features/updatesOptimistic/updatesOptimisticSlice';
import { Container } from './DismissedMenu.styles';
import { AffectedItemsDialog } from '../Components/AffectedItemsDialog/AffectedItemsDialog';

interface IDismissedMenu {
  updateStatus: IUpdateRawQueryList;
}

const DismissedMenu = ({ updateStatus }: IDismissedMenu) => {
  const params = useParams<{ id: string; equipmentId: string }>();

  const { toast } = useToast();

  const dispatch = useAppDispatch();

  const { role, updatesMtViewTime } = useContext(NavigationContext);

  const queryClient = useQueryClient();

  const openDeleteDialog = useHookState(false);

  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);

  const [openAffectedItemsDialog, setOpenAffectedItemsDialog] =
    useState<boolean>(false);

  const mutation = useMutation(changeUpdateStatus, {
    onSuccess: async newUpdate => {
      dispatch(addBackUpUpdatedUpdates(newUpdate));
      dispatch(addUpdatedUpdate({ newUpdate, updatesMtViewTime }));

      toast.success('Update changed successfully');
      queryClient.invalidateQueries(['updateStatuses']);
      queryClient.invalidateQueries(['updatesMetrics']);
      queryClient.invalidateQueries(['updateStatus', updateStatus.id]);
      queryClient.invalidateQueries(['studios', params.id, 'updateStatuses']);
      queryClient.invalidateQueries([
        'equipments',
        params.equipmentId,
        'updateStatuses',
      ]);
    },
    onError: () => {
      toast.success('Something got wrong');
    },
  });

  const mutationDelete = useMutation(deleteUpdate, {
    onSuccess: async () => {
      toast.success('Update was deleted successfully');
      openDeleteDialog.set(false);

      dispatch(
        addDeletedUpdate({ updateId: updateStatus.id, updatesMtViewTime }),
      );

      queryClient.invalidateQueries(['updateStatuses']);
      queryClient.invalidateQueries(['updatesMetrics']);
      queryClient.invalidateQueries(['updateStatus', updateStatus.id]);
      queryClient.invalidateQueries(['studios', params.id, 'updateStatuses']);
      queryClient.invalidateQueries([
        'equipments',
        params.equipmentId,
        'updateStatuses',
      ]);
    },
    onError: () => {
      toast.error('Something got wrong');
    },
  });

  const options = () => {
    const initialOptions = [
      {
        label: 'See Affected Assets',
        iconName: 'cube',
        onClick: () => {
          setOpenAffectedItemsDialog(true);
        },
        bottomSeparator: false,
      },
      {
        disabled: ![InviteUserRole.ADMIN].includes(role),
        disableToolTip: [InviteUserRole.ADMIN].includes(role),
        toolTipText: 'Only admins can manage an update.',
        toolTipId: 'resetTooltip',
        label: updateStatus.priority
          ? 'Return to Prioritized'
          : 'Return to New',
        iconName: 'reset',
        onClick: () => {
          if (!mutation.isLoading) {
            if (updateStatus.priority) {
              mutation.mutate({
                updateStatusId: updateStatus.usId,
                type: UpdateStatusType.PRIORITIZED,
              });
            } else {
              mutation.mutate({
                updateStatusId: updateStatus.usId,
                type: UpdateStatusType.NEW,
              });
            }
          }
        },
        bottomSeparator: false,
      },
    ];

    if (updateStatus.usStudioId) {
      initialOptions.unshift({
        disabled: ![InviteUserRole.ADMIN].includes(role),
        disableToolTip: [InviteUserRole.ADMIN].includes(role),
        toolTipText: 'Only admins can delete an update.',
        toolTipId: 'deleteTooltip',
        label: 'Delete Update',
        iconName: 'trash',
        onClick: () => {
          openDeleteDialog.set(true);
        },
        bottomSeparator: true,
      });
      initialOptions.unshift({
        disabled: ![InviteUserRole.ADMIN].includes(role),
        disableToolTip: [InviteUserRole.ADMIN].includes(role),
        toolTipText: 'Only admins can edit an update.',
        toolTipId: 'editTooltip',
        label: 'Edit Update',
        iconName: 'edit',
        onClick: () => {
          setOpenEditDialog(true);
        },
        bottomSeparator: false,
      });
    }

    return initialOptions;
  };

  return (
    <Container>
      <GlobalMenu
        align="end"
        hoverColor="#E6E8F0"
        title="Options"
        itemList={options()}
      />

      {openEditDialog && (
        <CreateUpdatesDialog
          open={openEditDialog}
          edit={{ updatePrev: updateStatus }}
          onClose={() => setOpenEditDialog(false)}
        />
      )}

      <DeleteDialog
        open={openDeleteDialog}
        onDelete={() => {
          mutationDelete.mutate(updateStatus.id);
        }}
      />

      {openAffectedItemsDialog && (
        <AffectedItemsDialog
          open={openAffectedItemsDialog}
          onClose={() => {
            setOpenAffectedItemsDialog(false);
          }}
          updateId={updateStatus.id}
        />
      )}
    </Container>
  );
};

export { DismissedMenu };
